import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Select from '../../../../components/ui/Select';
import SkillAutocomplete from './SkillAutocomplete';
import { createSkill } from '../../../../api/Employee/skills';
import { ProficiencyLevel, SkillView } from '../../../../models/employee';

interface AddSkillModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { skillName: string; proficiency: ProficiencyLevel }) => void | Promise<void>;
  /**
   * Failure from the PARENT's save call (`addUserSkill`), which is the step this modal cannot see.
   * `createError` below only covers creating the skill in the company catalogue; the save that
   * actually attaches it to the employee happens in `onSubmit`, and when the server rejected that
   * with a 400 the modal used to close anyway and the user was told nothing at all. The parent now
   * keeps the modal open and passes the reason down here.
   */
  submitError?: string | null;
}

const AddSkillModal: React.FC<AddSkillModalProps> = ({ isOpen, onClose, onSubmit, submitError }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    skillName: '',
    proficiency: ProficiencyLevel.Awareness
  });

  const [createError, setCreateError] = useState('');
  // The save is the parent's async call, so the modal awaits it to drive the button's loader.
  const [saving, setSaving] = useState(false);

  const proficiencyOptions = [
    { value: ProficiencyLevel.Awareness, label: t('Awareness') },
    { value: ProficiencyLevel.Working, label: t('Working') },
    { value: ProficiencyLevel.Strong, label: t('Strong') }
  ];

  // A name nobody has used yet is the normal case here, so the field offers to add it rather than
  // leaving the user to guess whether typing it was enough.
  const handleCreateSkill = async (name: string): Promise<SkillView | null> => {
    setCreateError('');
    try {
      const result = await createSkill({ name });
      if (result.errors.length > 0 || !result.payload) {
        setCreateError(result.errors[0]?.description || t('skills.createSkillFailed'));
        return null;
      }
      return result.payload;
    } catch {
      setCreateError(t('skills.createSkillFailed'));
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e?.preventDefault();
    if (saving) return;
    if (formData?.skillName && formData?.proficiency) {
      // Do NOT clear the form here. The save is the parent's async job and it can fail; wiping the
      // fields optimistically is what turned a rejected save into silent data loss — the modal was
      // left showing an empty form (or closed outright) with the typed skill gone. On success the
      // parent unmounts this modal, which resets the state anyway.
      setSaving(true);
      try {
        await onSubmit(formData);
      } finally {
        setSaving(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Icon name="Award" size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{t('Add Skill')}</h2>
              <p className="text-sm text-muted-foreground">{t('Add a new skill with proficiency level')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label={t('Close')}
            className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <SkillAutocomplete
            label={t('Skill Name')}
            placeholder={t('Search or enter a skill...')}
            value={formData?.skillName}
            onChange={(value) => setFormData({ ...formData, skillName: value })}
            onCreate={handleCreateSkill}
            required
          />
          {createError && (
            <p className="text-sm text-destructive" data-testid="skill-create-error">{createError}</p>
          )}

          <Select
            label={t('Proficiency Level')}
            options={proficiencyOptions}
            value={formData?.proficiency}
            onChange={(value) => setFormData({ ...formData, proficiency: value as ProficiencyLevel })}
            required
          />

          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              <strong>{t('Proficiency Levels')}:</strong><br />
              • <strong>{t('Awareness')}:</strong> {t('Basic understanding')}<br />
              • <strong>{t('Working')}:</strong> {t('Can apply independently')}<br />
              • <strong>{t('Strong')}:</strong> {t('Advanced capability')}
            </p>
          </div>

          {submitError && (
            <div
              className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive"
              data-testid="skill-submit-error"
              role="alert"
            >
              {submitError}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Plus"
              disabled={!formData.skillName.trim() || saving}
              loading={saving}
              data-testid="add-skill-submit"
            >
              {t('Add Skill')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSkillModal;
